import type { ResponseConfig } from "@kubb/plugin-client/clients/axios";
import type { UseQueryResult } from "@tanstack/react-query";
import type { Cursor } from "kubb";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import NewApiPaginatedTable from "./NewApiPaginatedTable";

/** Shallow object equality — we only compare top-level param values. */
function shallowEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

export interface FilterFormProps {
  refresh: () => void;
}

export default function NewGetListPaginatedTable<
  TItem,
  TQuery,
  TData = TItem[],
>(props: {
  header: [React.ReactNode, number] | null;
  cursor?: Cursor;
  row: (
    item: TItem,
    update: (updatedItem: any) => void,
  ) => React.ReactElement<{ key: string }>;
  "data-cy"?: string; // optional cypress label for the table
  tableClassName?: string;
  noDataRow?: React.ReactElement;
  showPagination?: boolean;
  additionalFunctionButtons?: React.ReactElement;
  filterForm?: ({ refresh }: FilterFormProps) => React.ReactElement;
  params: TQuery;
  useQuery: (
    variables: TQuery,
  ) => UseQueryResult<ResponseConfig<TData> | undefined, any>;
  lastUpdateRequested?: Date;
  showRefreshing?: boolean;
  /**
   * Callback that receives the fetched data array.
   * Use this to track data changes for features like conditional auto-refresh.
   */
  onDataFetched?: (data: TItem[]) => void;
}) {
  const initialCursor = props.cursor || { max_items: 25 };
  const [cursor, setCursor] = useState<Cursor>(initialCursor);
  const [lastUpdateRequested, setLastUpdateRequested] = useState(
    props.lastUpdateRequested,
  );

  const { useQuery, params, ...rest } = props;

  // Reset to the first page whenever the filter params change. Without this,
  // an after/before cursor from a previous result set leaks into the query
  // for a freshly filtered list and returns an unrelated or empty page.
  // Shallow-compare rather than JSON.stringify so key-order differences
  // don't trigger spurious resets.
  const previousParamsRef = useRef(params);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset is keyed on params reference change
  useEffect(() => {
    if (
      !shallowEqual(
        previousParamsRef.current as Record<string, unknown>,
        params as Record<string, unknown>,
      )
    ) {
      previousParamsRef.current = params;
      setCursor(initialCursor);
    }
  }, [params]);

  const { data, isPending, error, refetch, isRefetching } = useQuery({
    ...params,
    ...cursor,
  });

  useEffect(() => {
    if (
      props.lastUpdateRequested &&
      props.lastUpdateRequested !== lastUpdateRequested
    ) {
      setLastUpdateRequested(props.lastUpdateRequested);
      refetch();
    }
  }, [props.lastUpdateRequested, lastUpdateRequested, refetch]);

  // Notify parent when data is fetched
  useEffect(() => {
    if (data && props.onDataFetched) {
      props.onDataFetched(data.data as Array<TItem>);
    }
  }, [data, props.onDataFetched]);

  return (
    <>
      {/*{props.filterForm?.({ refresh })}*/}
      <NewApiPaginatedTable
        data={{
          data: data ? (data.data as Array<TItem>) : [],
          cursor: data?.headers?.["x-cursor"],
          setCursor: (cursor) => {
            if (cursor) {
              setCursor(cursor);
            }
          },
        }}
        error={error ? (error as unknown as Error) : null}
        loading={isPending || (props.showRefreshing && isRefetching)}
        refresh={refetch}
        // updateItem={updateItem}
        {...rest}
      />
    </>
  );
}
